import { useEffect, useState } from "react";
import {
  Box, Typography, Chip, CircularProgress,
  Alert, TextField, Tooltip, IconButton,
  Accordion, AccordionSummary, AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Check, ChartNoAxesCombined, CirclePlus, Info, TrendingUp, TrendingDown, Minus, CircleGauge } from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis,
  CartesianGrid, Tooltip as RechartsTooltip, ReferenceLine,
} from "recharts";
import { useTheme, alpha } from "@mui/material/styles";
import { useTranslation } from "../../../../../language";
import axios from "axios";
import { BPLIST } from "../../../../../constants";
import CustomDialogSmall from "../../../../../components/Dialog/DiaCompSmall";
import CustomDialog from "../../../../../components/Dialog/DiaCompMedium";
import ButtonForm from "../../../../../components/Button/ButtonForm";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function evalBp(sys, dia) {
  if (sys == null || dia == null) return null;
  if (sys < 121 && dia < 81) return "NORMAL";
  if (sys < 131 && dia < 89) return "ELEVATED";
  return "HIGH";
}

function count7day(records) {
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - 7);
  return records.filter(r => new Date(r.m_date + "T00:00:00") >= cutoff).length;
}

const accordionSx = (theme) => ({
  borderRadius: "16px !important",
  overflow: "hidden",
  "&:before": { display: "none" },
  bgcolor: theme.palette.mode === "dark" ? alpha("#fff", 0.04) : alpha("#fff", 0.5),
  backdropFilter: "blur(28px) saturate(180%)",
  WebkitBackdropFilter: "blur(28px) saturate(180%)",
  border: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.65)"}`,
  boxShadow: theme.palette.mode === "dark"
    ? "inset 0 1px 0 rgba(255,255,255,0.06), 0 8px 32px rgba(0,0,0,0.55)"
    : "inset 0 1px 0 rgba(255,255,255,0.7), 0 4px 24px rgba(0,0,0,0.06)",
});

const summarySx = {
  px: 2.5,
  bgcolor: "transparent",
  minHeight: 44,
  "& .MuiAccordionSummary-content": { my: 0.5, display: "flex", alignItems: "center", gap: 1, pr: 2 },
};

function compute7dayAvg(records) {
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - 7);
  const recent = records.filter(r => new Date(r.m_date + "T00:00:00") >= cutoff);
  if (!recent.length) return { sys: null, dia: null };
  const sys = Math.round(recent.reduce((s, r) => s + r.systole_mmHg, 0) / recent.length);
  const dia = Math.round(recent.reduce((s, r) => s + r.diastole_mmHg, 0) / recent.length);
  return { sys, dia };
}

// ─── Chart ────────────────────────────────────────────────────────────────────

function BpChartContent({ records, theme, t }) {
  const green  = theme.palette.traffic.green;
  const orange = theme.palette.traffic.orange;
  const red    = theme.palette.traffic.red;
  const primary    = theme.palette.primary.main;
  const secondary  = theme.palette.secondary.main;

  const chartPoints = [...records]
    .sort((a, b) => new Date(a.m_date) - new Date(b.m_date))
    .map(r => ({
      id:     r.id,
      date:   r.m_date,
      label:  new Date(r.m_date + "T00:00:00").toLocaleDateString("en", { month: "short", day: "numeric", year: "2-digit" }),
      sys:    r.systole_mmHg,
      dia:    r.diastole_mmHg,
      time:   r.m_type_display,
      status: evalBp(r.systole_mmHg, r.diastole_mmHg),
    }));

  if (!chartPoints.length) return (
    <Typography sx={{ p: 3 }} color="text.secondary">{t("vitals_no_chart_data")}</Typography>
  );

  const allNums = [
    ...chartPoints.map(d => d.sys),
    ...chartPoints.map(d => d.dia),
    130, 85,
  ];
  const span = Math.max(...allNums) - Math.min(...allNums) || 1;
  const pad  = span * 0.25;
  const yMin = parseFloat((Math.min(...allNums) - pad).toFixed(0));
  const yMax = parseFloat((Math.max(...allNums) + pad).toFixed(0));

  const dotColor = s => s === "NORMAL" ? green : s === "ELEVATED" ? orange : red;

  const SysDot = ({ cx, cy, payload }) => (
    <circle cx={cx} cy={cy} r={5}
      fill={dotColor(payload.status)}
      stroke={theme.palette.background.paper} strokeWidth={2} />
  );

  const DiaDot = ({ cx, cy, payload }) => (
    <circle cx={cx} cy={cy} r={4}
      fill={dotColor(payload.status)}
      stroke={theme.palette.background.paper} strokeWidth={1.5} />
  );

  return (
    <Box sx={{ width: "100%", pt: 2, px: 2 }}>
      {/* Legend */}
      <Box sx={{ display: "flex", gap: 1.5, justifyContent: "flex-end", mb: 1, flexWrap: "wrap" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Box sx={{ width: 20, height: 2.5, bgcolor: primary, borderRadius: 1 }} />
          <Typography variant="caption" color="text.secondary">{t("vitals_bp_chart_sys")}</Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Box sx={{ width: 20, height: 2, bgcolor: secondary, borderRadius: 1, opacity: 0.7 }} />
          <Typography variant="caption" color="text.secondary">{t("vitals_bp_chart_dia")}</Typography>
        </Box>
      </Box>

      <ResponsiveContainer width="100%" height={260}>
        <LineChart data={chartPoints} margin={{ top: 16, right: 28, left: 4, bottom: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} vertical={false} />
          <ReferenceLine y={130} stroke={green} strokeDasharray="5 3" strokeWidth={1.5}
            label={{ value: `${t("vitals_bp_chart_sys")}: 130`, position: "insideTopRight", fontSize: 10, fill: green, fontWeight: 600 }} />
          <ReferenceLine y={85} stroke={green} strokeDasharray="5 3" strokeWidth={1.5}
            label={{ value: `${t("vitals_bp_chart_dia")}: 85`, position: "insideBottomRight", fontSize: 10, fill: green, fontWeight: 600 }} />
          <XAxis dataKey="label"
            tick={{ fontSize: 11, fill: theme.palette.text.secondary, fontWeight: 500 }}
            axisLine={{ stroke: theme.palette.text.primary, strokeWidth: 0.75 }}
            tickLine={false} padding={{ left: 32, right: 32 }} />
          <YAxis domain={[yMin, yMax]}
            tick={{ fontSize: 11, fill: theme.palette.text.secondary, fontWeight: 500 }}
            axisLine={{ stroke: theme.palette.text.primary, strokeWidth: 0.75 }}
            tickLine={false} width={40}
            tickFormatter={v => Math.round(v)} />
          <RechartsTooltip
            contentStyle={{
              borderRadius: 10, border: `1px solid ${theme.palette.divider}`,
              backgroundColor: theme.palette.background.paper,
              boxShadow: "0 4px 16px rgba(0,0,0,0.12)", fontSize: 13,
            }}
            formatter={(value, name) => [
              `${value} mmHg`,
              name === "sys" ? t("vitals_bp_chart_sys") : t("vitals_bp_chart_dia"),
            ]}
          />
          <Line type="monotone" dataKey="sys" stroke={primary} strokeWidth={2.5}
            dot={<SysDot />} activeDot={{ r: 9, stroke: primary, strokeWidth: 2 }} />
          <Line type="monotone" dataKey="dia" stroke={secondary} strokeWidth={2}
            strokeDasharray="5 4"
            dot={<DiaDot />} activeDot={{ r: 8, stroke: secondary, strokeWidth: 2 }} />
        </LineChart>
      </ResponsiveContainer>

      {/* History table */}
      <Box sx={{ mt: 3, borderRadius: 2, overflow: "hidden", border: "1px solid", borderColor: "divider" }}>
        <Box sx={{
          display: "grid", gridTemplateColumns: "1.4fr 0.8fr 0.8fr 0.8fr 0.8fr",
          px: 2, py: 1,
          bgcolor: theme.palette.mode === "light" ? "grey.C200" : "greydark.C800",
        }}>
          {[t("bt_chart_col_date"), t("vitals_bp_col_time"), t("vitals_bp_chart_sys"), t("vitals_bp_chart_dia"), t("bt_chart_col_status")].map(h => (
            <Typography key={h} variant="caption"
              sx={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, color: "text.secondary", fontSize: "0.7rem" }}>
              {h}
            </Typography>
          ))}
        </Box>
        {[...chartPoints].reverse().map((row, idx, arr) => {
          const prev      = arr[idx + 1];
          const delta     = prev != null ? row.sys - prev.sys : null;
          const TrendIcon = delta == null ? null
            : Math.abs(delta) < 1 ? Minus
            : delta > 0 ? TrendingUp : TrendingDown;
          const isEven = idx % 2 === 0;
          return (
            <Box key={row.id} sx={{
              display: "grid", gridTemplateColumns: "1.4fr 0.8fr 0.8fr 0.8fr 0.8fr",
              px: 2, py: 0.5, alignItems: "center",
              bgcolor: isEven
                ? (theme.palette.mode === "light" ? "grey.C50"  : "greydark.C900")
                : (theme.palette.mode === "light" ? "grey.C100" : "greydark.C900"),
            }}>
              <Typography variant="body2" color="text.secondary">
                {new Date(row.date + "T00:00:00").toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}
              </Typography>
              <Typography variant="body2" color="text.secondary">{row.time}</Typography>
              <Typography variant="body2" fontWeight={600} sx={{ fontVariantNumeric: "tabular-nums" }}>
                {row.sys}
              </Typography>
              <Typography variant="body2" fontWeight={600} sx={{ fontVariantNumeric: "tabular-nums" }}>
                {row.dia}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                {row.status === "NORMAL" ? (
                  <Check size={16} strokeWidth={3} color={green} />
                ) : row.status === "ELEVATED" ? (
                  <Chip label={t("vitals_bp_status_elevated")} size="small"
                    sx={{ bgcolor: orange, color: "#fff", fontWeight: 700, fontSize: "0.6rem", height: 20, borderRadius: "6px", "& .MuiChip-label": { px: 0.75 } }}
                  />
                ) : (
                  <Chip label={t("vitals_status_high")} size="small"
                    sx={{ bgcolor: red, color: "#fff", fontWeight: 700, fontSize: "0.6rem", height: 20, borderRadius: "6px", "& .MuiChip-label": { px: 0.75 } }}
                  />
                )}
                {TrendIcon && (
                  <TrendIcon size={14}
                    color={delta > 0 ? red : green}
                    strokeWidth={2} />
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

// ─── Info ─────────────────────────────────────────────────────────────────────

function BpInfoContent({ t }) {
  return (
    <Box sx={{ px: 1, pb: 2 }}>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
        {t("vitals_bp_info_p1")}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
        {t("vitals_bp_info_p2")}
      </Typography>
      <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
        {t("vitals_bp_info_ht")}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
        {t("vitals_bp_info_p3")}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
        {t("vitals_bp_info_p4")}
      </Typography>
      <Typography variant="body2" color="text.disabled">
        {t("vitals_bp_info_source")}
      </Typography>
    </Box>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function BpPanel() {
  const theme    = useTheme();
  const { t }    = useTranslation();

  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  // dialog open states
  const [chartOpen, setChartOpen] = useState(false);
  const [addOpen,   setAddOpen]   = useState(false);
  const [infoOpen,  setInfoOpen]  = useState(false);

  // add form state
  const [addDate,    setAddDate]    = useState("");
  const [addType,    setAddType]    = useState("M");
  const [addSys,     setAddSys]     = useState("");
  const [addDia,     setAddDia]     = useState("");
  const [addLoading, setAddLoading] = useState(false);
  const [addError,   setAddError]   = useState("");
  const [addSuccess, setAddSuccess] = useState(false);

  useEffect(() => {
    axios.get(BPLIST)
      .then(res => {
        setRecords(Array.isArray(res.data) ? res.data : []);
        setLoading(false);
      })
      .catch(err => { setError(err.message); setLoading(false); });
  }, []);

  const refresh = () => {
    axios.get(BPLIST)
      .then(res => setRecords(Array.isArray(res.data) ? res.data : []))
      .catch(() => {});
  };

  const openAddDialog = () => {
    setAddDate(new Date().toISOString().split("T")[0]);
    setAddType("M");
    setAddSys("");
    setAddDia("");
    setAddError("");
    setAddSuccess(false);
    setAddOpen(true);
  };

  const closeAddDialog = () => {
    setAddOpen(false);
    setAddError("");
    setAddSuccess(false);
  };

  const handleAddSubmit = async () => {
    if (!addDate || !addSys || !addDia) {
      setAddError(t("vitals_fill_all_fields"));
      return;
    }
    setAddLoading(true);
    setAddError("");
    try {
      await axios.post(BPLIST, {
        m_date:        addDate,
        m_type:        addType,
        systole_mmHg:  parseInt(addSys, 10),
        diastole_mmHg: parseInt(addDia, 10),
      });
      setAddSuccess(true);
      refresh();
      setTimeout(() => { setAddOpen(false); setAddSuccess(false); }, 1400);
    } catch (err) {
      const d = err.response?.data;
      setAddError(
        typeof d === "string" ? d :
        d?.detail ?? d?.m_date?.[0] ?? d?.systole_mmHg?.[0] ?? t("vitals_save_error_generic")
      );
    } finally {
      setAddLoading(false);
    }
  };

  // ── Early returns ─────────────────────────────────────────────────────────

  if (loading) return (
    <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
      <CircularProgress size={18} />
    </Box>
  );

  if (error) return (
    <Alert severity="error" sx={{ mx: 2.5, my: 1 }}>
      <Typography variant="body2">{error}</Typography>
    </Alert>
  );

  // ── Derive values ─────────────────────────────────────────────────────────

  const { sys: sys7d, dia: dia7d } = compute7dayAvg(records);
  const status    = evalBp(sys7d, dia7d);
  const color     = status === "NORMAL"   ? theme.palette.traffic.green
                  : status === "ELEVATED" ? theme.palette.traffic.orange
                  : status === "HIGH"     ? theme.palette.traffic.red
                  : theme.palette.grey.C400;
  const valueStr  = sys7d != null ? `${sys7d} / ${dia7d}` : "—";
  const count7d   = count7day(records);

  return (
    <>
      <Accordion defaultExpanded disableGutters elevation={0} sx={accordionSx(theme)}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={summarySx}>
          <Typography variant="subtitle2" fontWeight={700}
            sx={{ textTransform: "uppercase", letterSpacing: 0.4 }}>
            {t("vitals_blood_pressure")}
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mr: 1 }}>
            <CircleGauge size={14} color={theme.palette.text.secondary} />
            <Typography variant="caption" color="text.secondary">
              {t("vitals_bp_7day_count")}: {count7d}
            </Typography>
          </Box>
        </AccordionSummary>

        <AccordionDetails sx={{ p: 0 }}>
          <Box sx={{ bgcolor: "transparent" }}>
            <Box sx={{ display: "flex", alignItems: "center", px: 2.5, py: 0.75, gap: { xs: 1, sm: 3 }, flexWrap: "nowrap" }}>

              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, flexGrow: 1, minWidth: { xs: 0, sm: 120 } }}>
                <Box sx={{ width: 8, height: 8, borderRadius: "50%", flexShrink: 0, bgcolor: color, mt: "7px" }} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body1" fontWeight={500}>{t("vitals_bp_7day_avg")}</Typography>
                  <Typography variant="caption" color="text.secondary">{t("vitals_unit")}: mmHg</Typography>
                </Box>
              </Box>

              <Box sx={{ minWidth: 90, textAlign: "center", flexShrink: 0, display: { xs: "none", sm: "block" } }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", fontSize: "0.6rem", letterSpacing: 0.3 }}>
                  {t("vitals_ref")}:
                </Typography>
                <Typography variant="caption" fontWeight={600} color="text.secondary">
                  120 / 80 mmHg
                </Typography>
              </Box>

              <Box sx={{ minWidth: { xs: "auto", sm: 80 }, display: "flex", justifyContent: "flex-end", flexShrink: 0 }}>
                {status === "NORMAL" ? (
                  <Check size={18} strokeWidth={3} color={theme.palette.traffic.green} />
                ) : status != null ? (
                  <Chip
                    label={t(status === "ELEVATED" ? "vitals_bp_status_elevated" : "vitals_status_high")}
                    size="small"
                    sx={{ bgcolor: color, color: "#fff", fontWeight: 700, fontSize: "0.65rem", height: 22, borderRadius: "6px", "& .MuiChip-label": { px: 1 } }}
                  />
                ) : null}
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 0.25, sm: 1.5 } }}>
                <Typography variant="body1" fontWeight={500} sx={{ fontVariantNumeric: "tabular-nums" }}>
                  {valueStr}
                </Typography>
                <Tooltip title={t("vitals_bp_chart_title")} placement="top">
                  <IconButton size="small" onClick={() => setChartOpen(true)}
                    sx={{ "&:hover": { transform: "scale(1.15)" } }}>
                    <ChartNoAxesCombined size={18} color={theme.palette.primary.main} />
                  </IconButton>
                </Tooltip>
                <Tooltip title={t("vitals_bp_add_title")} placement="top">
                  <IconButton size="small" onClick={openAddDialog}
                    sx={{ "&:hover": { transform: "scale(1.15)" } }}>
                    <CirclePlus size={18} color={theme.palette.text.secondary} />
                  </IconButton>
                </Tooltip>
                <Tooltip title={t("vitals_bp_info_title")} placement="top">
                  <IconButton size="small" onClick={() => setInfoOpen(true)}
                    sx={{ "&:hover": { transform: "scale(1.15)" } }}>
                    <Info size={18} color={theme.palette.text.secondary} />
                  </IconButton>
                </Tooltip>
              </Box>

            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* ── Chart dialog ───────────────────────────────────────────────────── */}
      <CustomDialog
        open={chartOpen}
        onClose={() => setChartOpen(false)}
        title={t("vitals_bp_chart_title")}
        content={<BpChartContent records={records} theme={theme} t={t} />}
      />

      {/* ── Add dialog ─────────────────────────────────────────────────────── */}
      <CustomDialogSmall
        open={addOpen}
        onClose={closeAddDialog}
        title={t("vitals_bp_add_title")}
        content={
          <Box sx={{ pt: 1, pb: 1, px: 0.5 }}>
            {addSuccess ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 2 }}>
                <Check size={20} color={theme.palette.traffic.green} strokeWidth={2.5} />
                <Typography fontWeight={500} color="success.main">{t("vitals_save_success")}</Typography>
              </Box>
            ) : (
              <>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 0.5 }}>
                  <TextField
                    label={t("vitals_date")} type="date" size="small"
                    value={addDate}
                    onChange={e => { setAddDate(e.target.value); setAddError(""); }}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                  <TextField
                    label={t("vitals_bp_time_of_day")} size="small"
                    value={addType}
                    onChange={e => { setAddType(e.target.value); setAddError(""); }}
                    fullWidth
                    select
                    SelectProps={{ native: true }}
                  >
                    <option value="M">{t("vitals_bp_morning")}</option>
                    <option value="N">{t("vitals_bp_noon")}</option>
                    <option value="E">{t("vitals_bp_evening")}</option>
                  </TextField>
                  <TextField
                    label={`${t("vitals_bp_systole")} (mmHg)`} type="number" size="small"
                    value={addSys}
                    onChange={e => { setAddSys(e.target.value); setAddError(""); }}
                    inputProps={{ min: 50, max: 300, step: 1 }}
                    fullWidth
                  />
                  <TextField
                    label={`${t("vitals_bp_diastole")} (mmHg)`} type="number" size="small"
                    value={addDia}
                    onChange={e => { setAddDia(e.target.value); setAddError(""); }}
                    inputProps={{ min: 30, max: 200, step: 1 }}
                    fullWidth
                  />
                </Box>
                {addError && (
                  <Typography variant="caption" color="error" sx={{ display: "block", mb: 1 }}>
                    {addError}
                  </Typography>
                )}
                <ButtonForm
                  name={addLoading ? <CircularProgress size={18} color="inherit" /> : t("vitals_height_save")}
                  onClick={handleAddSubmit}
                  disabled={addLoading || !addDate || !addSys || !addDia}
                />
              </>
            )}
          </Box>
        }
      />

      {/* ── Info dialog ────────────────────────────────────────────────────── */}
      <CustomDialog
        open={infoOpen}
        onClose={() => setInfoOpen(false)}
        title={t("vitals_bp_info_title")}
        content={<BpInfoContent t={t} />}
      />
    </>
  );
}
